import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Add, Delete, GroupsOutlined } from '@mui/icons-material';
import { getCapitalPartnersBrief } from '../../../services/request/CapitalManagement';
import type { CapitalPartner } from '../../../models/StockPickTypes';
import { formatCurrency } from '../../../utils/CurrencyFormat';

export interface DebitPartnerCapitalRow {
  partnerId: string;
  allocatedAmount: number;
  partnerRatePctMonthly?: number;
  note?: string;
}

export interface DebitPartnerCapitalSectionProps {
  rows: DebitPartnerCapitalRow[];
  onChange: (rows: DebitPartnerCapitalRow[]) => void;
  /** Debit outstanding — used for validation hint. */
  outstanding?: number;
  disabled?: boolean;
}

const emptyRow = (): DebitPartnerCapitalRow => ({
  partnerId: '',
  allocatedAmount: 0,
  partnerRatePctMonthly: 1.5,
});

const filterPartners = createFilterOptions<CapitalPartner>({
  stringify: (p) => `${p.name} ${p.availableBalance ?? 0}`,
});

const DebitPartnerCapitalSection: React.FC<DebitPartnerCapitalSectionProps> = ({
  rows,
  onChange,
  outstanding = 0,
  disabled,
}) => {
  const [partners, setPartners] = useState<CapitalPartner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCapitalPartnersBrief()
      .then(setPartners)
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  const totalAllocated = useMemo(
    () => rows.reduce((s, r) => s + (r.allocatedAmount > 0 ? r.allocatedAmount : 0), 0),
    [rows],
  );

  const sortedPartners = useMemo(
    () => [...partners].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [partners],
  );

  const updateRow = (index: number, patch: Partial<DebitPartnerCapitalRow>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => onChange([...rows, emptyRow()]);

  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  const overLimit = outstanding > 0 && totalAllocated > outstanding + 0.01;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'grey.50' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <GroupsOutlined color="primary" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Partner capital funding (Capitals)
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Optionally fund this debit from partner capital. Allocations appear in Control Centre and Capital Partners → Lending.
        Partner receives 1.5%/mo; borrower rate comes from the debit above.
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading partners…</Typography>
      ) : partners.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          No capital partners yet — add partners under Capitals → Capital Partners first.
        </Alert>
      ) : (
        <>
          {rows.length === 0 && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0 }}>
              No partner capital linked — click Add partner to fund from Capitals.
            </Alert>
          )}
          <Stack spacing={1.5}>
            {rows.map((row, idx) => {
              const partner = partners.find((p) => p.id === row.partnerId) ?? null;
              return (
                <Grid
                  key={`${idx}-${row.partnerId}`}
                  container
                  spacing={1}
                  alignItems="center"
                >
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      disabled={disabled}
                      options={sortedPartners}
                      value={partner}
                      onChange={(_, p) => updateRow(idx, { partnerId: p?.id ?? '' })}
                      getOptionLabel={(p) => p.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      filterOptions={filterPartners}
                      ListboxProps={{ style: { maxHeight: 240 } }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Stack direction="row" justifyContent="space-between" width="100%">
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              avail {formatCurrency(option.availableBalance ?? 0)}
                            </Typography>
                          </Stack>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Partner" placeholder="Search…" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Amount"
                      type="number"
                      disabled={disabled}
                      value={row.allocatedAmount || ''}
                      onChange={(e) => updateRow(idx, { allocatedAmount: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Rate %/mo"
                      type="number"
                      disabled={disabled}
                      value={row.partnerRatePctMonthly ?? 1.5}
                      onChange={(e) => updateRow(idx, { partnerRatePctMonthly: Number(e.target.value) || 1.5 })}
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={10} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Note"
                      disabled={disabled}
                      value={row.note ?? ''}
                      onChange={(e) => updateRow(idx, { note: e.target.value })}
                      placeholder="Optional"
                    />
                  </Grid>
                  <Grid item xs={2} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      color="error"
                      disabled={disabled}
                      onClick={() => removeRow(idx)}
                      aria-label="Remove row"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={addRow}
            disabled={disabled}
            sx={{ mt: 1.5 }}
          >
            Add partner
          </Button>
          {totalAllocated > 0 && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: overLimit ? 'error.main' : 'text.secondary' }}
            >
              Partner capital total: {formatCurrency(totalAllocated)}
              {outstanding > 0 && ` · Outstanding: ${formatCurrency(outstanding)}`}
              {overLimit && ' — exceeds outstanding'}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default DebitPartnerCapitalSection;
